import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBackendURL } from '../utils';
import ProgressTabs from '../components/ProgressTabs';
import SweatTextTitle from '../steps/SweatTextTitle';
import RemindersInfo from './RemindersInfo';
import { useAuth } from '../auth';
import '../BasicReminder.css';

const StepTrainerSMSConsent = () => {
  const { user, fetchUserInfo } = useAuth();
  const [consent, setConsent] = useState(false);
  const [syncingConsent, setSyncingConsent] = useState(false);
  const [syncedConsent, setSyncedConsent] = useState(false);
  const [syncError, setSyncError] = useState('');
  const [userSettingsID, setUserSettingsID] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSpinner, setShowSpinner] = useState(false);
  const navigate = useNavigate();
  const debounceConsentRef = useRef();
  const lastSyncedConsentRef = useRef(false);

  useEffect(() => {
    if (!user || !user.role.includes('trainer')) {
      alert('You must be a trainer to access this step. If you are indeed a trainer, please contact our Instagram for account support.');
      navigate('/tools/reminders/step1');
      return;
    }
    const initialConsent = !!user?.settings?.sms_opt_in;
    setConsent(initialConsent);
    lastSyncedConsentRef.current = initialConsent;
    setUserSettingsID(user?.settings?.id);
    setLoading(false);
  }, [user, navigate]);



  
    // Call fetchUserInfo when component mounts to ensure we have the latest user data
    useEffect(() => {
      fetchUserInfo();
    }, []);
  

  // Debounced consent PUT
  useEffect(() => {
    if (loading || userSettingsID == null || consent === lastSyncedConsentRef.current) return;
    if (debounceConsentRef.current) clearTimeout(debounceConsentRef.current);
    debounceConsentRef.current = setTimeout(async () => {
      setSyncingConsent(true);
      setSyncedConsent(false);
      setSyncError('');
      try {
        const res = await fetch(`${getBackendURL()}/user_settings/${userSettingsID}?user_id=${user.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${window.localStorage.getItem('ptc_access_token') || ''}`
          },
          body: JSON.stringify({ sms_opt_in: consent }),
        });
        if (!res.ok) throw new Error('Failed to update SMS opt-in');
        lastSyncedConsentRef.current = consent;
        setSyncedConsent(true);
      } catch (err) {
        setSyncError(err.message);
      } finally {
        setSyncingConsent(false);
      }
    }, 2000);
    return () => clearTimeout(debounceConsentRef.current);
  }, [consent, userSettingsID, loading]);

  useEffect(() => {
    if (!syncingConsent) setShowSpinner(false);
  }, [syncingConsent]);

  // If no user redirect to homepage.
  if (!user) {
    return (
      <div className="reminder-container fogged-card">
        Sorry, you must be logged in to access this page.
      </div>
    );
  } 
  return (
    <div className="reminder-container fogged-card">
      <SweatTextTitle />
      <RemindersInfo />
      <ProgressTabs currentStep={2} />
      <h2 className="step-title">Step 3: SMS Consent</h2>
      <p>To receive SMS reminders, you must opt in. You can change this later in your account settings.</p>
      <div style={{ margin: '1rem 0', fontSize: '1rem', display: 'flex', alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center' }}>
          {showSpinner && (
            <span className="spinner" style={{
              width: '2rem',
              height: '1rem',
              border: '5px solid #ccc',
              borderTop: '2px solid #000',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginLeft: '0.5rem',
            }}></span>
          )}
          <input
            type="checkbox"
            checked={consent}
            onChange={e => {
              setConsent(e.target.checked);
              setShowSpinner(true);
            }}
            disabled={syncingConsent}
            style={{ marginRight: '0.5rem' }}
          />
          I agree to receive ongoing transactional and marketing SMS from SweatText
          and ThePersonalTrainersClub.com. 
          These may include appointment reminders or check-ins. 
          It may also include system 
          messages from this site about your account. 
          Message & data rates may apply. 
          Reply STOP to unsubscribe at any time.
          {syncError && syncingConsent && (
            <span style={{ color: 'red', marginLeft: '0.5rem', fontSize: '0.95rem' }}>{syncError}</span>
          )}
        </label>
      </div>
      <div style={{ margin: '1rem 0' }}>
        {syncedConsent && <span style={{ color: 'green', fontWeight: 'bold', marginLeft: '1rem' }}>✔ Changes saved!</span>}
      </div>
      <div style={{ marginTop: '1rem' }}>
        By opting in, you agree to our{' '}
        <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
        <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
        <br />
        You can also adjust preferences later via your account settings once you've created an account.
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
        <button className="ptc-secondary-button" onClick={() => navigate('/tools/reminders/step-trainer-phone')} style={{ padding: '1rem 2rem', fontSize: '1rem' }}>
          Previous Step
        </button>
        <button
          className="ptc-button"
          onClick={() => {
            // THe fetchUserInfo didnt seem to propogate user state...
            // window.location.href='tools/reminders/step-trainer-test';
            navigate('/tools/reminders/step-trainer-test');

          }}
          style={{ padding: '1rem 2rem', 
            background: consent && !syncingConsent ? '#6610f2' : '#ccc', 
            color: consent && !syncingConsent ? 'white' : '#222', 
            border: 'none', 
            cursor: consent && !syncingConsent ? 'pointer' : 'not-allowed',
            fontSize: '1rem' }}
          disabled={!consent || syncingConsent}
        >
          Next Step
        </button>
      </div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default StepTrainerSMSConsent;
